import { action, makeObservable, observable } from "mobx";
import { fetchProtectedResource, isNumeric } from "../../utilities";
import { message } from "antd";
import BaseService from "../base/BaseService";
import {
	RULES_FILE_GET_FILES_FOR_VALIDATION,
	RULES_FILE_DELETE_FILE,
	RULES_FILE_GET_FILES_FOR_ARCHIVE,
	RULES_FILE_DOWNLOAD_FILE,
} from "../../configs/api-paths";

/**
 * Service dealing with rate files.
 */
class RateFilesService extends BaseService {
	/**
	 * All rate files.
	 * @type {[]}
	 */
	@observable
	rateFilesData = [];

	/**
	 * Is the service loading data.
	 * @type {boolean}
	 */
	@observable
	loading = false;

	/**
	 * Rate files table pagination object.
	 * @type {{}}
	 */
	@observable
	pagination = {};

	/**
	 * Rate files table sorting object.
	 * @type {{}}
	 */
	@observable
	sorter = {
		field: "uploadedAt",
		order: "descend",
	};

	@observable
	search = "";

	constructor(props) {
		super(props);
		makeObservable(this);
	}

	@action
	setRateFilesData = (data) => {
		this.rateFilesData = data;
	};

	@action
	setLoading = (loading) => {
		this.loading = loading;
	};

	@action
	setPagination = (pagination) => {
		this.pagination = pagination;
	};

	@action
	setSorter = (sorter) => {
		this.sorter = sorter;
	};

	@action
	setSearch = (search) => {
		this.search = search;
	};

	loadRateFilesValidationData = async () => {
		await this.loadRateFilesData(`${RULES_FILE_GET_FILES_FOR_VALIDATION}?`);
	};

	loadRateFilesArchiveData = async () => {
		await this.loadRateFilesData(`${RULES_FILE_GET_FILES_FOR_ARCHIVE}?`);
	};

	/**
	 * @returns {Promise<void>}
	 */
	loadRateFilesData = async (url) => {
		this.setLoading(true);
		let hideLoadingMessage = message.loading("Loading...", 0);

		try {


			const currentPage = this.pagination.current;
			const pageSize = this.pagination.pageSize;



			if (isNumeric(currentPage) && isNumeric(pageSize)) {
				url += `page=${currentPage - 1}&size=${pageSize}`;
			}

			if (this.search.length > 0) {
				url += `&name=${this.search}`;
			}

			const result = await fetchProtectedResource(url);
			hideLoadingMessage();
			const pagedResults = await result.json();

			this.setPagination({
				...this.pagination,
				total: pagedResults.totalElements,
			});
			this.setRateFilesData(pagedResults);
		} catch (error) {
			console.log(error);
			hideLoadingMessage();
		} finally {
			this.setLoading(false);
		}
	};

	deleteRateFile = async (rateFileId, rateFileName, deletedBy) => {
		let hideLoadingMessage = message.loading("Deleting...", 0);

		try {
			let url = `${RULES_FILE_DELETE_FILE.replace("{id}", rateFileId)}`;
			await fetchProtectedResource(url + "?deletedBy=" + deletedBy, {
				method: "PATCH",
			});
			hideLoadingMessage();
			message.success("Successfully deleted rate file " + rateFileName, 5);
			return true;
		} catch (error) {
			hideLoadingMessage();
			message.error(
				`Failed deleting rate file ${rateFileName}. Error: ${error}`,
				10
			);
			return false;
		}
	};

	downloadRateFile = async (rateFileName) => {
		let hideLoadingMessage = message.loading("Downloading...", 0);

		try {
			const url = `${RULES_FILE_DOWNLOAD_FILE.replace(
				"{fileName}",
				rateFileName
			)}`;
			await fetchProtectedResource(url).then((response) => {
				response.blob().then((blob) => {
					const url = window.URL.createObjectURL(blob);
					const a = document.createElement("a");
					a.href = url;
					a.download = rateFileName;
					a.click();
				});
				hideLoadingMessage();
			});
		} catch (error) {
			hideLoadingMessage();
			message.error(
				`Failed downloading rate file ${rateFileName}. Error: ${error}`,
				10
			);
		}
	};
}

export default RateFilesService;
